import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Flow } from '../../core/utils/flow-utils';
import { FormDataService } from '../../core/services/form-data-service';
import { SignupAPIService } from '../../core/services/signup-apiservice';
import { NavigationService } from '../../core/services/navigation-service';
import { HttpClient } from '@angular/common/http';
import {
  passwordsMatchValidator,
  strongPasswordValidator,
  usernameValidator,
  emailFormatValidator,
} from '../../core/validators/validator';
import { TextService } from '../../core/services/text-service';

@Component({
  selector: 'app-shared-cloud-account-form',
  templateUrl: './shared-cloud-account-form.html',
  styleUrl: './shared-cloud-account-form.scss',
  standalone: false,
})
export class SharedCloudAccountForm {
  passcodeStatus: 'PENDING' | 'INVALID' | 'VERIFIED' | null = null;
  accountForm!: FormGroup;
  showPassword = false;
  showConfirm = false;
  flow: Flow;
  sessionId: string | null = null;
  timer = 0;
  interval: any;
  emailVerified = false;

  constructor(
    private fb: FormBuilder,
    private formData: FormDataService,
    private signupApi: SignupAPIService,
    private navService: NavigationService,
    public textService: TextService,
    private http: HttpClient
  ) {
    this.flow = this.navService.getFlow();
    const root = document.querySelector('app-top-menu');
    if (this.flow == 'cloud') {
      root?.classList.remove('menu-open');
    }
  }

  ngOnInit(): void {
    const saved = this.formData.getCloudAccountData();

    this.accountForm = this.fb.group(
      {
        email: this.fb.control(
          { value: saved?.email || '', disabled: !!saved?.verified },
          [Validators.required, emailFormatValidator()]
        ),
        Passcode: this.fb.control({ value: '', disabled: true }),
        username: this.fb.control(
          { value: saved?.username || '', disabled: !saved?.verified },
          {
            validators: [Validators.required, Validators.minLength(4), Validators.maxLength(15)],
            asyncValidators: [usernameValidator(this.signupApi)],
            updateOn: 'blur',
          }
        ),
        password: this.fb.control(
          { value: saved?.password || '', disabled: !saved?.verified },
          [Validators.required, strongPasswordValidator]
        ),
        confirmPassword: this.fb.control(
          { value: saved?.confirmPassword || '', disabled: !saved?.verified },
          [Validators.required]
        ),
      },
      { validators: [passwordsMatchValidator] }
    );

    if (saved?.sessionId) this.sessionId = saved.sessionId;

    if (saved?.verified) {
      this.passcodeStatus = 'VERIFIED';
      this.emailVerified = true;

      this.accountForm.get('username')?.enable();
      this.accountForm.get('password')?.enable();
      this.accountForm.get('confirmPassword')?.enable();

      const usernameCtrl = this.accountForm.get('username');

      // 🔹 Force async validator to run again (Angular skips if value didn't change)
      if (saved?.username) {
        const current = usernameCtrl?.value;
        usernameCtrl?.setValue(current + ' ', { emitEvent: false }); // add space
        usernameCtrl?.setValue(current, { emitEvent: true });        // revert → triggers API call
      }
    } else {
      this.accountForm.get('username')?.disable();
      this.accountForm.get('password')?.disable();
      this.accountForm.get('confirmPassword')?.disable();
    }

    // 🔹 Track username validation for overall form validity
    const usernameCtrl = this.accountForm.get('username');
    usernameCtrl?.statusChanges.subscribe(status => {
      if (status === 'PENDING') {
        this.accountForm.setErrors({ usernamePending: true });
      } else if (status === 'VALID') {
        const errors = { ...(this.accountForm.errors || {}) };
        delete errors['usernamePending'];
        delete errors['usernameInvalid'];
        this.accountForm.setErrors(Object.keys(errors).length ? errors : null);
      } else if (status === 'INVALID') {
        this.accountForm.setErrors({ usernameInvalid: true });
      }
    });
  }


  startTimer(seconds: number) {
    this.timer = seconds;
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.timer > 0) this.timer--;
      else clearInterval(this.interval);
    }, 1000);
  }

  get timerDisplay(): string {
    const m = Math.floor(this.timer / 60);
    const s = this.timer % 60;
    return `Resend Code ${m}:${s.toString().padStart(2, '0')}`;
  }

  onValidateEmail() {
    const emailCtrl = this.accountForm.get('email');
    if (!emailCtrl?.value) return;

    this.signupApi.validateEmail(emailCtrl.value).subscribe({
      next: (res) => {
        this.sessionId = res.sessionId;
        this.startTimer(res.expireInSeconds);
        this.accountForm.get('Passcode')?.enable();

        this.formData.setCloudAccountData({
          email: emailCtrl.value,
          sessionId: res.sessionId,
          verified: false,
        });
      },
      error: (err) => console.error('Email validation failed:', err),
    });
  }

  onResendEmail() {
    if (!this.sessionId) return;
    const email = this.accountForm.get('email')?.value;
    if (!email) return;

    this.signupApi.resendEmail(this.sessionId, email).subscribe({
      next: () => this.startTimer(300),
      error: (err) => console.error('Resend failed:', err),
    });
  }

  onValidatePasscode() {
    const emailCtrl = this.accountForm.get('email');
    const passCtrl = this.accountForm.get('Passcode');
    if (!emailCtrl || !passCtrl) return;

    const sessionId = this.sessionId;
    if (!sessionId) return;

    passCtrl.disable();
    this.passcodeStatus = 'PENDING';

    this.signupApi.verifyPasscode(sessionId, emailCtrl.value, passCtrl.value).subscribe({
      next: (res: any) => {
        if (res?.status === 'VERIFIED') {
          this.passcodeStatus = 'VERIFIED';
          this.emailVerified = true;

          emailCtrl.disable();
          passCtrl.disable();
          clearInterval(this.interval);

          this.accountForm.get('username')?.enable();
          this.accountForm.get('password')?.enable();
          this.accountForm.get('confirmPassword')?.enable();

          this.formData.setCloudAccountData({
            email: emailCtrl.value,
            sessionId,
            verified: true,
          });

          return;
        }

        if (res?.status === 'CODE_INVALID') {
          this.passcodeStatus = 'INVALID';
          passCtrl.enable();
          return;
        }

        this.handleInvalidSession();
      },
      error: (err) => {
        console.error('Verification error:', err);
        if (err?.error?.message?.includes('Invalid Session Id')) {
          this.handleInvalidSession();
        } else {
          this.passcodeStatus = 'INVALID';
          passCtrl.enable();
        }
      },
    });
  }

  handleInvalidSession() {
    this.passcodeStatus = 'INVALID';
    this.sessionId = null;
    this.timer = 0;
    this.emailVerified = false;
    clearInterval(this.interval);

    const emailCtrl = this.accountForm.get('email');
    const passCtrl = this.accountForm.get('Passcode');
    const usernameCtrl = this.accountForm.get('username');
    const passwordCtrl = this.accountForm.get('password');
    const confirmCtrl = this.accountForm.get('confirmPassword');

    emailCtrl?.enable();
    passCtrl?.reset();
    passCtrl?.disable();
    usernameCtrl?.reset();
    usernameCtrl?.disable();
    passwordCtrl?.reset();
    passwordCtrl?.disable();
    confirmCtrl?.reset();
    confirmCtrl?.disable();

    this.startTimer(0);
    this.formData.clearCloudAccountData();
  }

  getValue() {
    this.accountForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    if (!this.canContinue()) {
      this.accountForm.markAllAsTouched();
      console.warn('⚠️ Form not ready, username not validated yet.');
      return null;
    }

    this.formData.setCloudAccountData({
      email: this.accountForm.get('email')?.value,
      username: this.accountForm.get('username')?.value,
      password: this.accountForm.get('password')?.value,
      confirmPassword: this.accountForm.get('confirmPassword')?.value,
      sessionId: this.sessionId || undefined,
      verified: this.emailVerified,
    });

    return this.accountForm.value;
  }



  markAllTouched() {
    this.accountForm.markAllAsTouched();
    this.accountForm.updateValueAndValidity();
  }

  canContinue(): boolean {
    const usernameCtrl = this.accountForm.get('username');

    // Still waiting for async validation
    if (this.accountForm.pending || usernameCtrl?.pending) return false;

    // Username invalid or not verified
    if (!usernameCtrl || usernameCtrl.invalid) return false;

    // Whole form invalid
    if (!this.accountForm.valid) return false;

    return true;
  }
}
